import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useContext, useMemo, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  useCreatePriorAuthListMutation,
  useDeletePriorAuthListMutation,
  useGetPriorAuthListsQuery,
  useLazyGetPriorAuthListQuery,
  useUpdatePriorAuthListMutation,
} from "store/queries/priorAuthorizations";
import styled from "styled-components";
import { IPriorAuthListSummary } from "types/PriorAuth/PriorAuth";

import {
  draftFromList,
  emptyDraft,
  IPriorAuthListDraft,
  PriorAuthListDialog,
} from "./PriorAuthListDialog";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const Intro = styled.p`
  margin: 0;
  color: var(--gray-700);
  max-width: 60rem;
`;

function extractErrorMessage(e: unknown): string {
  const err = e as {
    data?: { message?: string; error?: string } | string;
    message?: string;
  };
  const data = err?.data;
  if (typeof data === "string") return data;
  return (
    data?.message ?? data?.error ?? err?.message ?? "An unknown error occurred."
  );
}

/**
 * CLE-2758 Part 1 — the Auth tab under Payer Rules.
 *
 * A prior-auth list is a set of payers, an optional set of plans, and the CPT codes those payers
 * require prior authorization for. Lists are GLOBAL (Ani): every account sees the same ones, because
 * an auth requirement is the payer's policy rather than any one facility's contract.
 *
 * One list can cover several payers on purpose — Ani asked for "one file and one update" rather than
 * the same codes repeated once per payer.
 *
 * Nothing consumes these lists yet; the Requires Authorization badge on an estimate is CLE-2766.
 */
export function PriorAuthLists() {
  const { current: toastElement } = useContext(ToastContext);

  const { data: payersData, isFetching: loadingPayers } =
    useGetAllPayersQuery();
  const { data: listsData, isFetching: loadingLists } =
    useGetPriorAuthListsQuery();

  const [fetchList] = useLazyGetPriorAuthListQuery();
  const [createList, { isLoading: isCreating }] =
    useCreatePriorAuthListMutation();
  const [updateList, { isLoading: isUpdating }] =
    useUpdatePriorAuthListMutation();
  const [deleteList] = useDeletePriorAuthListMutation();

  const [dialogVisible, setDialogVisible] = useState(false);
  const [draft, setDraft] = useState<IPriorAuthListDraft>(emptyDraft());

  const lists = listsData?.data ?? [];

  const payerOptions = useMemo(
    () =>
      (payersData?.data ?? [])
        .map((payer: any) => ({ label: payer.name, value: payer.id }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    [payersData]
  );

  const payerNameById = useMemo(() => {
    const map = new Map<number, string>();
    payerOptions.forEach((option: any) => map.set(option.value, option.label));
    return map;
  }, [payerOptions]);

  const openNew = () => {
    setDraft(emptyDraft());
    setDialogVisible(true);
  };

  const openEdit = async (row: IPriorAuthListSummary) => {
    try {
      // The table row carries only a count — the codes are fetched when they're about to be shown.
      const response = await fetchList(row.id).unwrap();
      setDraft(draftFromList(response.data));
      setDialogVisible(true);
    } catch (e) {
      toastElement?.show({
        severity: "error",
        summary: "Could not open list",
        detail: extractErrorMessage(e),
      });
    }
  };

  const handleSave = async (next: IPriorAuthListDraft) => {
    try {
      if (next.id) {
        await updateList({
          id: next.id,
          name: next.name,
          payerIds: next.payerIds,
          planKeywords: next.planKeywords,
          cptCodes: next.cptCodes,
          file: next.file,
        }).unwrap();
      } else {
        await createList({
          name: next.name,
          payerIds: next.payerIds,
          planKeywords: next.planKeywords,
          cptCodes: next.cptCodes,
          file: next.file,
        }).unwrap();
      }
      setDialogVisible(false);
      toastElement?.show({
        severity: "success",
        summary: next.id ? "List saved" : "List created",
      });
    } catch (e) {
      toastElement?.show({
        severity: "error",
        summary: "Could not save list",
        detail: extractErrorMessage(e),
      });
    }
  };

  const handleDownload = async (row: IPriorAuthListSummary) => {
    try {
      const response = await fetchList(row.id).unwrap();
      // Header matches what the importer reads, so a downloaded file re-uploads cleanly.
      const csv = ["CPT Code", ...response.data.cptCodes].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName =
        row.name.replace(/[^a-z0-9_-]+/gi, "-").replace(/^-+|-+$/g, "") ||
        "prior-auth-list";
      link.setAttribute("download", `${safeName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      toastElement?.show({
        severity: "error",
        summary: "Could not download list",
        detail: extractErrorMessage(e),
      });
    }
  };

  const handleDelete = (row: IPriorAuthListSummary) => {
    confirmDialog({
      message: `Delete "${row.name}" and its ${row.cptCodeCount} CPT codes? This cannot be undone.`,
      header: "Delete prior auth list",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await deleteList(row.id).unwrap();
          toastElement?.show({ severity: "success", summary: "List deleted" });
        } catch (e) {
          toastElement?.show({
            severity: "error",
            summary: "Could not delete list",
            detail: extractErrorMessage(e),
          });
        }
      },
    });
  };

  const payersBody = (row: IPriorAuthListSummary) => {
    if (row.payerIds.length === 0) return <Tag value="All payers" />;
    return (
      <div className="flex flex-wrap gap-1">
        {row.payerIds.map((id) => (
          <Tag key={id} value={payerNameById.get(id) ?? `Payer ${id}`} />
        ))}
      </div>
    );
  };

  const plansBody = (row: IPriorAuthListSummary) => {
    if (row.planKeywords.length === 0)
      return <span className="text-gray-500">Any plan</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {row.planKeywords.map((keyword) => (
          <Tag key={keyword} value={keyword} severity="info" />
        ))}
      </div>
    );
  };

  const sourceBody = (row: IPriorAuthListSummary) =>
    row.source ?? <span className="text-gray-500">Entered by hand</span>;

  const updatedBody = (row: IPriorAuthListSummary) =>
    new Date(row.updatedAt).toLocaleString();

  const actionsBody = (row: IPriorAuthListSummary) => (
    <div className="flex gap-1">
      <Button
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${row.name}`}
        onClick={() => openEdit(row)}
      />
      <Button
        icon="pi pi-download"
        className="p-button-text p-button-sm"
        aria-label={`Download ${row.name}`}
        onClick={() => handleDownload(row)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Delete ${row.name}`}
        onClick={() => handleDelete(row)}
      />
    </div>
  );

  return (
    <Wrapper>
      <HeaderRow>
        <Intro>
          CPT codes each payer requires prior authorization for. A list can
          cover several payers, so a change is made once rather than repeated
          per payer.
        </Intro>
        <Button icon="pi pi-plus" className="p-button-raised" onClick={openNew}>
          New Prior Auth List
        </Button>
      </HeaderRow>

      <DataTable
        value={lists}
        loading={loadingLists}
        dataKey="id"
        emptyMessage="No prior auth lists yet."
        responsiveLayout="scroll"
      >
        <Column field="name" header="Name" sortable />
        <Column header="Payers" body={payersBody} />
        <Column header="Plans" body={plansBody} />
        <Column field="cptCodeCount" header="CPT codes" sortable />
        <Column field="source" header="Source" body={sourceBody} />
        <Column
          field="updatedAt"
          header="Updated"
          sortable
          body={updatedBody}
        />
        <Column header="Actions" body={actionsBody} />
      </DataTable>

      <PriorAuthListDialog
        visible={dialogVisible}
        value={draft}
        payerOptions={payerOptions}
        loadingPayers={loadingPayers}
        isNew={!draft.id}
        saving={isCreating || isUpdating}
        onSave={handleSave}
        onHide={() => setDialogVisible(false)}
      />
    </Wrapper>
  );
}

export default PriorAuthLists;
